import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { absoluteUrl } from '../api/client';
import { getInterviewByToken, issueRTCToken } from '../api/interview';
import type { InterviewPublic, RTCToken } from '../api/types';
import { useRTC } from '../hooks/useRTC';

export default function InterviewRoom() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [rtcInfo, setRtcInfo] = useState<RTCToken | null>(null);
  const [meta, setMeta] = useState<InterviewPublic | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    issueRTCToken(token).then(setRtcInfo).catch((e) => setFetchError(e.message));
    getInterviewByToken(token).then(setMeta).catch(() => {});
  }, [token]);

  const rtc = useRTC({ tokenInfo: rtcInfo, autoJoin: true });
  // 注:不再单独 getUserMedia(mic 已被 useRTC 录音 + SDK 各自捕获,第三次会冲突导致回声)
  // 麦克风音量由 useRTC 通过 SDK 的 onLocalAudioPropertiesReport 推送

  // ── AI 离会时(monitor 主动 stop 或豆包结束)→ 候选人自动结束 ──
  // 跟踪 aiPresent: true → false 的转换,延迟 3s 让最后的语音播完,然后 leave
  const aiPresentPrevRef = useRef(false);
  useEffect(() => {
    if (rtc.isMock) return;
    if (aiPresentPrevRef.current && !rtc.aiPresent) {
      // AI 刚刚离开房间 → 自动结束
      console.log('[InterviewRoom] AI left, auto-ending in 3s');
      const timer = window.setTimeout(async () => {
        try {
          await rtc.leave();
        } catch {}
        nav(`/interview/${token}/ended`);
      }, 3000);
      return () => window.clearTimeout(timer);
    }
    aiPresentPrevRef.current = rtc.aiPresent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.aiPresent, rtc.isMock]);

  // 远端 AI 音频流出现时 hook 到录音混音
  useEffect(() => {
    if (!rtc.aiPresent || rtc.isMock) return;
    let attached = false;
    const tryHook = () => {
      if (attached) return true;
      const audios = Array.from(document.querySelectorAll('audio'));
      for (const a of audios) {
        const s = (a as HTMLAudioElement).srcObject;
        if (s instanceof MediaStream && s.getAudioTracks().length > 0) {
          rtc.hookRemoteAudioToRecording?.(s);
          attached = true;
          return true;
        }
      }
      return false;
    };
    if (tryHook()) return;
    const interval = window.setInterval(() => { if (tryHook()) window.clearInterval(interval); }, 500);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.aiPresent, rtc.isMock]);

  const unlockAudio = useCallback(async () => {
    console.log('[unlockAudio] ===== user gesture triggered =====');
    const audios = Array.from(document.querySelectorAll('audio'));
    console.log(`[unlockAudio] ${audios.length} audio elements in DOM`);
    for (const [i, a] of audios.entries()) {
      const s = a.srcObject;
      const tracks = s instanceof MediaStream
        ? s.getTracks().map((t) => ({ kind: t.kind, readyState: t.readyState, enabled: t.enabled, muted: t.muted, label: t.label }))
        : null;
      console.log(`[unlockAudio] audio[${i}]`, {
        paused: a.paused, muted: a.muted, volume: a.volume,
        readyState: a.readyState, autoplay: a.autoplay,
        currentTime: a.currentTime, duration: a.duration,
        hasSrcObject: !!s, streamId: s instanceof MediaStream ? s.id : null,
        tracks,
      });
      a.muted = false;
      a.volume = 1;
      try {
        await a.play();
        console.log(`[unlockAudio] audio[${i}] play() OK — now paused=${a.paused}`);
      } catch (e: any) {
        console.warn(`[unlockAudio] audio[${i}] play() failed:`, e?.name, e?.message);
      }
    }
    // 尝试调 RTC SDK 主动方法
    try {
      const mod = (await import('@volcengine/rtc')) as any;
      console.log('[unlockAudio] SDK module keys:', Object.keys(mod));
    } catch (e) {
      console.warn('[unlockAudio] SDK import err:', e);
    }
  }, []);

  const handleLeave = async () => {
    // 1) 先关 RTC + 上传录音(并行)
    await rtc.leave();
    // 2) 通知 backend:候选人主动结束 → status=completed + 触发评估
    if (token) {
      try {
        await fetch(absoluteUrl(`/api/interviews/by-token/${token}/end`), { method: 'POST' });
      } catch (e) {
        console.warn('[InterviewRoom] notify end failed', e);
      }
    }
    nav(`/interview/${token}/ended`);
  };

  if (fetchError) return <div className="container"><div className="banner error">{fetchError}</div></div>;
  if (!rtcInfo) return <div className="container"><p className="hint">正在申请入会...</p></div>;

  return (
    <div className="container">
      <h1>面试进行中</h1>

      {rtc.isMock && (
        <div className="banner warn">
          ⚠️ 当前为开发模式(Mock Token),RTC 不会真实接入。
        </div>
      )}

      {rtc.status === 'error' && <div className="banner error">入会失败:{rtc.error}</div>}

      <div className="card">
        <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div className="video-tile" ref={rtc.localVideoRef}>
              <div className="label">你(候选人)</div>
            </div>
            <div style={{ marginTop: 8 }}>
              <span className={`mic-indicator ${rtc.micLevel > 0.05 ? 'on' : ''}`}></span>
              <span className="hint">麦克风音量:{Math.round(rtc.micLevel * 100)}%</span>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div className="video-tile" style={{ background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Avatar value={meta?.interviewer_avatar} name={meta?.interviewer_name} size={96} />
                </div>
                <div className="label" style={{ position: 'static', background: 'transparent', marginTop: 12 }}>
                  面试官 · {meta?.interviewer_name || '李先生'}
                </div>
                <div className="hint" style={{ marginTop: 8 }}>
                  {rtc.aiPresent ? '● 在线' : '○ 等待入会'}
                </div>
              </div>
            </div>
            <div ref={rtc.remoteAudioContainerRef}></div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="danger" onClick={handleLeave}>结束并退出</button>
          <button className="ghost" onClick={unlockAudio} title="如果听不到面试官的声音,点一下">
            🔊 启用声音
          </button>
        </div>
      </div>
    </div>
  );
}

